import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { SelectionChangedEvent } from 'ag-grid-community';
import { AgGridModule } from 'ag-grid-angular';
import { GridOptions } from 'ag-grid-community';
import * as _ from 'underscore';
import { Connector, Mode } from '../../models/connector';
import { DrRequestInfo } from '../../models/dr-request-info';
import { OperationType, DataAreaType, Endpoint } from '../../models/enums';
import { GenericColDef, GridRowSelectionType } from '../../models/grid-context';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { GenericGridComponent } from '../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { GenericTreeComponent } from '../generic-tree/generic-tree.component';

@Component({
    standalone: false,
    selector: 'res-connectors-and-tree',
    templateUrl: './connectors-and-tree.component.html',
    styleUrls: ['./connectors-and-tree.component.scss'],
    providers: [AgGridModule],
})
export class ConnectorsAndTreeComponent implements OnInit {
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;
    @ViewChild(GenericTreeComponent, { static: true }) tree: GenericTreeComponent;

    gridOptions: GridOptions;
    selectedConnector: any;
    nodes = new Array<GenericTreeNode>();
    selectedFolder: any;
    dataAreaType: DataAreaType;
    isConnectorsBusy = false;
    isPathsBusy = false;
    selectedPath: string;
    connectors: Array<Connector>;
    dataAreaOperationType: OperationType;
    dataAreaConnectorHandle: string;
    parentNode = [];
    selectedOriginalPath: string;
    selectedNode: GenericTreeNode;
    parentNodeName = '';
    showPopover: boolean;

    readonly emptyPathParam = { name: '', handle: '', type: 'UNKNOWN', leaf: false };

    @Input() automationId: string;
    @Input() disableConnGrid: boolean;
    @Output() onCONChange = new EventEmitter();

    columnDefs: Array<GenericColDef>;
    rowSelection = GridRowSelectionType.single;
    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.adminOrgManager_listConnectors,
        responseName: 'connectors',
        data: {
            organizationName: this.commonService?.currentUser?.customerName
        }
    });

    rowDisableRules = {
        'disabled-row': () => {
            return this.disableConnGrid;
        },
        'disabled-selected-row': (params) => {
            if (this.disableConnGrid && params && params.data && params.data.handle === this.dataAreaConnectorHandle) {
                return true;
            }
            return false;
        }
    };

    responseCallback = (response: any) => {
        if (response) {
            this.showPopover = false;
            const connectors = new Array<Connector>();
            if (!response.connectors) {
                response.connectors = [];
            }
            response.connectors.forEach(element => {
                const connector = new Connector(element);
                connectors.push(connector);
            });

            response.connectors = _.filter(connectors, (connector) => connector.mode === Mode.READWRITE);
            response.connectors = _.sortBy(response.connectors, 'name');

            response.totalCount = response.numberResults || response.connectors.length;
            response.dataAreaConnectorHandle = this.dataAreaConnectorHandle;
        }
    }

    loadDataFinishedCallback = () => {
        if (this.dataAreaConnectorHandle) {
            this.selectedOriginalPath = this.selectedPath;
            this.genericGrid.gridApi.forEachNode((node) => {
                if (node.data.handle === this.dataAreaConnectorHandle) {
                    node.setSelected(true);
                }
            });
        }
    }

    onSelectionChanged = (event?: SelectionChangedEvent) => {
        this.showPopover = false;
        if (event && event.api) {
            this.selectedConnector = event.api.getSelectedRows()[0];
            const remotePath = this.selectedConnector && this.selectedConnector.remotePath ? this.selectedConnector.remotePath : '';
            this.parentNodeName = remotePath.substring(remotePath.lastIndexOf('/') + 1);
            this.onConnectorsAndTreeChange(null);
            this.nodes = [];
            this.selectedPath = '';
            if (this.selectedConnector) {
                this.loadPaths(this.selectedConnector.remotePath);
            }
        }
    }

    constructor(private restService: RestService, private commonService: CommonService) { }

    ngOnInit(): void {
        this.columnDefs = new Array<GenericColDef>(
            {
                field: 'name',
                headerName: 'Connector Name',
                automationId: this.automationId,
                lockVisible: true
            },
            {
                field: 'description',
                headerName: 'Description',
                automationId: this.automationId,
            },
            {
                field: 'type',
                headerName: 'Type',
                automationId: this.automationId,
                width: 110,
                suppressSizeToFit: true
            },
            {
                field: 'mode',
                headerName: 'Mode',
                automationId: this.automationId,
                width: 95,
                suppressSizeToFit: true
            },
            {
                field: 'remoteHost',
                headerName: 'Server',
                automationId: this.automationId,
            },
            {
                field: 'remotePath',
                headerName: 'Path',
                automationId: this.automationId,
            }
        );
    }

    loadNodeDataCallback = (node: GenericTreeNode) => {
        if (node) {
            this.loadPaths(node.data.handle, node);
        }
    }

    selectedNodeChangedCallback = (node: GenericTreeNode) => {
        if (node) {
            this.selectedNode = node;
            this.selectedPath = node.data.handle;
            this.onConnectorsAndTreeChange(node.data);
        }
    }

    loadPaths(path: string, parent?: GenericTreeNode): void {
        if (path) {
            let connectorFolderPath = {};
            if (parent) {
                parent.isLoading = true;
                connectorFolderPath = parent.data;
            } else {
                this.isPathsBusy = true;
                connectorFolderPath = this.emptyPathParam;
            }

            this.restService.post(Endpoint.connectorManager_exploreConnector, { connectorHandle: this.selectedConnector.handle, parentPath: connectorFolderPath }).then(response => {
                const paths = [];
                if (parent) {
                    parent.isLoading = false;
                } else {
                    this.isPathsBusy = false;
                }
                if (response && response.paths) {
                    response.paths.forEach(element => {
                        if (element.name !== '.' && element.name !== '..') {
                            paths.push(new GenericTreeNode({
                                name: element.name,
                                icon: 'fa-folder',
                                iconExpanded: 'fa-folder-open',
                                iconColor: 'color-yellow-07',
                                isExpandable: !element.leaf,
                                isExpandedOnSelect: !element.leaf,
                                hasExpanderPadding: true,
                                data: element
                            }));
                        }
                    });
                    if (parent) {
                        this.tree.setNodeData(parent, paths);
                        if (parent.children.length === 0) {
                            parent.isExpandable = false;
                            parent.iconExpanded = null;
                        }
                    } else {
                        parent = new GenericTreeNode({
                            name: this.parentNodeName, isExpandable: true, isExpandedOnSelect: true, isExpanded: true, customText: 'Connector Root',
                            hasExpanderPadding: true, data: { name: this.emptyPathParam.name, handle: '.', type: this.emptyPathParam.type, leaf: this.emptyPathParam.leaf }
                        });
                        this.nodes = [parent];
                        this.tree.setData(this.nodes);
                        this.tree.setNodeData(parent, paths);
                        this.isPathsBusy = false;
                    }

                    if (response.paths.length === 0 || (response.paths && response.paths.length === 1 && response.paths[0].name === '.')) {
                        this.showPopover = true;
                    }
                } else {
                    this.showPopover = false;
                }
                this.tree.setData(this.nodes);

                if (this.dataAreaConnectorHandle && this.dataAreaOperationType === OperationType.edit && this.selectedOriginalPath) {
                    const nodeToSelect = this.tree.getNodeBy(n => n.name && (this.selectedOriginalPath.indexOf(n.name) === 0 ||
                        this.selectedOriginalPath.indexOf('/' + n.name) === 0), parent);
                    if (nodeToSelect) {
                        this.selectedOriginalPath = this.selectedOriginalPath.substring(nodeToSelect.name.length + this.selectedOriginalPath.indexOf(nodeToSelect.name));
                        if (this.selectedOriginalPath.length === 0) {
                            this.selectedOriginalPath = null;
                        }
                        this.tree.selectNode(nodeToSelect);
                    }
                }
            }, () => {
                this.isPathsBusy = false;
            });
        }
    }

    onConnectorsAndTreeChange(selFolder: any): void {
        this.selectedFolder = selFolder;
        this.onCONChange.emit();
    }
}
